import {
  StrictMode,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type SyntheticEvent,
} from "react";
import { createRoot } from "react-dom/client";

import { introFrom } from "#src/guide-intro.ts";

import styles from "./admin.module.scss";
import { api } from "./api.ts";
import { copyText } from "./clipboard.ts";
import { formatCostUsd } from "./money.ts";

interface Usage {
  readonly cachedInputTokens: number;
  readonly costUsd: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly reasoningTokens: number;
  readonly totalTokens: number;
}

interface AdminUsage extends Usage {
  readonly unknownCostRuns: number;
}

interface Access {
  readonly completed: boolean;
  readonly login: string;
  readonly messages: number;
  readonly name: string;
  readonly password: string;
  readonly usage: Usage;
}

interface Brief {
  readonly accesses: ReadonlyArray<Access>;
  readonly completed: number;
  readonly createdAt: string;
  readonly kind: "test" | "work";
  readonly questionGuide: number;
  readonly slug: string;
  readonly title: string;
  readonly usage: AdminUsage;
}

const emptyAdminUsage: AdminUsage = {
  cachedInputTokens: 0,
  costUsd: 0,
  inputTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  totalTokens: 0,
  unknownCostRuns: 0,
};

function App() {
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [briefs, setBriefs] = useState<ReadonlyArray<Brief>>([]);
  const [creating, setCreating] = useState(false);
  const [usage, setUsage] = useState<AdminUsage>(emptyAdminUsage);
  const [changingSlug, setChangingSlug] = useState("");
  const [status, setStatus] = useState("");
  const createPanelRef = useRef<HTMLDetailsElement>(null);
  const [intro, setIntro] = useState("");
  const [introNote, setIntroNote] = useState("");
  // Вступление, подставленное из методички: его можно заменить другим файлом,
  // а то, что оператор вписал сам, новый файл не затирает.
  const guideIntroRef = useRef("");

  useEffect(() => {
    void api<{ ok: boolean }>("/api/admin/proverka", { method: "POST" }).then(
      async () => {
        setAuthorized(true);
        await refresh();
      },
      () => {
        setAuthorized(false);
      },
    );
  }, []);

  async function refresh(): Promise<void> {
    const result = await api<{
      oprosy: ReadonlyArray<Brief>;
      usage: AdminUsage;
    }>("/api/oprosy", { method: "POST" });
    setBriefs(result.oprosy);
    setUsage(result.usage);
  }

  async function login(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError("");
    try {
      await api("/api/admin/proverka", {
        headers: { "X-Admin": code.trim() },
        method: "POST",
      });
      setAuthorized(true);
      setCode("");
      await refresh();
    } catch {
      setError("Код не подошёл");
    }
  }

  async function create(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    setCreating(true);
    setError("");
    try {
      await api("/api/opros", { body: new FormData(form), method: "POST" });
      form.reset();
      setIntro("");
      setIntroNote("");
      guideIntroRef.current = "";
      await refresh();
      createPanelRef.current?.removeAttribute("open");
      setStatus("Рабочий бриф создан и появился в списке.");
    } catch (caught: unknown) {
      setError(
        caught instanceof Error ? caught.message : "Не удалось создать бриф",
      );
    } finally {
      setCreating(false);
    }
  }

  async function pickGuide(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = event.currentTarget.files?.[0];
    if (file === undefined) {
      return;
    }
    const found = introFrom(await file.text());
    const typedByHand = intro.trim() !== "" && intro !== guideIntroRef.current;
    if (!typedByHand) {
      setIntro(found);
      guideIntroRef.current = found;
    }
    setIntroNote(introHint(found !== "", typedByHand));
  }

  async function changeKind(brief: Brief, kind: Brief["kind"]): Promise<void> {
    setChangingSlug(brief.slug);
    setError("");
    setStatus("");
    try {
      await api(`/api/admin/opros/${encodeURIComponent(brief.slug)}/tip`, {
        body: JSON.stringify({ tip: kind }),
        headers: { "Content-Type": "application/json; charset=utf-8" },
        method: "POST",
      });
      await refresh();
      setStatus(
        kind === "test"
          ? `«${brief.title}» убран в тестовые.`
          : `«${brief.title}» возвращён в рабочие.`,
      );
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Не удалось изменить раздел брифа",
      );
    } finally {
      setChangingSlug("");
    }
  }

  if (authorized === null) {
    return <main className={styles.root} />;
  }
  if (!authorized) {
    return (
      <main className={styles.root}>
        <form
          className={styles.login}
          onSubmit={(event) => {
            void login(event);
          }}
        >
          <div className={styles.eyebrow}>Брифер · админка</div>
          <h1 className={styles.title}>Вход</h1>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-code">
              Код админки
            </label>
            <input
              id="admin-code"
              autoComplete="current-password"
              type="password"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          </div>
          <p className={styles.error}>{error}</p>
          <button className={styles.button} type="submit">
            Войти
          </button>
        </form>
      </main>
    );
  }

  const workBriefs = briefs.filter((brief) => brief.kind === "work");
  const testBriefs = briefs.filter((brief) => brief.kind === "test");
  const testUsage = sumUsage(testBriefs.map((brief) => brief.usage));

  return (
    <main className={styles.root}>
      <header className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Брифер · админка</div>
          <h1 className={styles.title}>Брифы</h1>
          <p className={styles.lede}>
            Рабочие брифы и расходы по всем прохождениям.
          </p>
        </div>
        <div className={styles.totalCost} aria-label="Общая стоимость">
          <span>Общая стоимость</span>
          <strong>{aggregateCost(usage)}</strong>
          <small>Все брифы, включая тестовые</small>
          <UnknownCost usage={usage} />
        </div>
      </header>
      <details ref={createPanelRef} className={styles.createPanel}>
        <summary>Новый бриф</summary>
        <form
          className={styles.createForm}
          onSubmit={(event) => {
            void create(event);
          }}
        >
          <div className={styles.field}>
            <label className={styles.label} htmlFor="guide">
              Методичка (.md)
            </label>
            <input
              id="guide"
              name="metodichka"
              accept=".md,.markdown,.txt"
              required
              type="file"
              onChange={(event) => {
                void pickGuide(event);
              }}
            />
            <p className={styles.hint}>
              О чём расспросить, зачем и где границы.
            </p>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="title">
              Название
            </label>
            <input
              id="title"
              name="nazvanie"
              placeholder="Пилот в трёх подразделениях"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="intro">
              Вступление
            </label>
            <textarea
              id="intro"
              name="vstuplenie"
              rows={4}
              value={intro}
              onChange={(event) => {
                setIntro(event.currentTarget.value);
              }}
            />
            {introNote === "" ? null : (
              <p className={styles.hint}>{introNote}</p>
            )}
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="questions">
                Вопросов, ориентир
              </label>
              <input
                id="questions"
                name="voprosov"
                defaultValue="12"
                max="40"
                min="3"
                type="number"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="accesses">
                Сколько доступов
              </label>
              <input
                id="accesses"
                name="dostupov"
                defaultValue="5"
                max="60"
                min="1"
                type="number"
              />
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="names">
              Имена, по одному в строке
            </label>
            <textarea id="names" name="imena" rows={3} />
          </div>
          <button className={styles.button} disabled={creating} type="submit">
            {creating ? "Создаю" : "Создать ссылку"}
          </button>
        </form>
      </details>
      <p className={styles.error}>{error}</p>
      <p className={styles.status} aria-live="polite">
        {status}
      </p>
      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <h2>Рабочие брифы</h2>
          <span>{workBriefs.length.toString()}</span>
        </div>
        <div className={styles.cards}>
          {workBriefs.length === 0 ? (
            <p className={styles.lede}>Рабочих брифов пока нет.</p>
          ) : null}
          {workBriefs.map((brief) => (
            <BriefCard
              key={brief.slug}
              brief={brief}
              changing={changingSlug === brief.slug}
              onChangeKind={changeKind}
            />
          ))}
        </div>
      </section>
      {testBriefs.length > 0 ? (
        <details className={styles.testSection}>
          <summary>
            <span>Тестовые и приёмочные</span>
            <span>
              {testBriefs.length.toString()} · {aggregateCost(testUsage)}
            </span>
          </summary>
          <p className={styles.hint}>
            Автопроверки и брифы, убранные из рабочего списка.
          </p>
          <div className={styles.cards}>
            {testBriefs.map((brief) => (
              <BriefCard
                key={brief.slug}
                brief={brief}
                changing={changingSlug === brief.slug}
                onChangeKind={changeKind}
              />
            ))}
          </div>
        </details>
      ) : null}
    </main>
  );
}

function BriefCard({
  brief,
  changing,
  onChangeKind,
}: {
  readonly brief: Brief;
  readonly changing: boolean;
  readonly onChangeKind: (brief: Brief, kind: Brief["kind"]) => Promise<void>;
}) {
  const link = `${location.origin}/b/${brief.slug}`;
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  async function copy(): Promise<void> {
    const copied = await copyText(link);
    setCopyState(copied ? "copied" : "failed");
    setTimeout(() => {
      setCopyState("idle");
    }, 1600);
  }

  return (
    <article className={styles.card}>
      <div className={styles.cardHeading}>
        <div>
          <h3>{brief.title}</h3>
          <p className={styles.meta}>
            {brief.createdAt.slice(0, 16).replace("T", " ")} · ориентир{" "}
            {brief.questionGuide} · прошли {brief.completed} из{" "}
            {brief.accesses.length}
          </p>
        </div>
        <div className={styles.briefCost}>
          <span>Стоимость брифа</span>
          <strong>{aggregateCost(brief.usage)}</strong>
          <UnknownCost usage={brief.usage} />
        </div>
      </div>
      <div className={styles.linkBox}>
        <code>{link}</code>
        <button
          className={styles.quietButton}
          type="button"
          onClick={() => {
            void copy();
          }}
        >
          {copyState === "copied"
            ? "Скопировано"
            : copyState === "failed"
              ? "Не удалось скопировать"
              : "Скопировать"}
        </button>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Кто</th>
              <th scope="col">Логин</th>
              <th scope="col">Код</th>
              <th scope="col">Состояние</th>
            </tr>
          </thead>
          <tbody>
            {brief.accesses.map((access) => (
              <tr key={access.login}>
                <td>{access.name || "—"}</td>
                <td>
                  <code>{access.login}</code>
                </td>
                <td>
                  <code>{access.password}</code>
                </td>
                <td>
                  {access.completed ? (
                    <a
                      className={styles.resultLink}
                      href={`/admin/itog/${brief.slug}/${access.login}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      документ готов
                    </a>
                  ) : access.messages > 0 ? (
                    `идёт, реплик ${access.messages.toString()}`
                  ) : (
                    "не начинал"
                  )}
                  {access.usage.totalTokens > 0 ? usageText(access.usage) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.cardActions}>
        <button
          className={styles.textButton}
          disabled={changing}
          type="button"
          onClick={() => {
            void onChangeKind(brief, brief.kind === "work" ? "test" : "work");
          }}
        >
          {changing
            ? "Переношу…"
            : brief.kind === "work"
              ? "Убрать в тестовые"
              : "Вернуть в рабочие"}
        </button>
      </div>
    </article>
  );
}

function UnknownCost({ usage }: { readonly usage: AdminUsage }) {
  if (usage.unknownCostRuns === 0) {
    return null;
  }
  return (
    <small className={styles.unknownCost}>
      У {usage.unknownCostRuns.toString()} прохожд. стоимость не сохранена
    </small>
  );
}

function aggregateCost(usage: AdminUsage): string {
  if (usage.costUsd > 0) {
    return formatCostUsd(usage.costUsd);
  }
  return usage.unknownCostRuns > 0 ? "Сумма неизвестна" : "Расхода пока нет";
}

function sumUsage(usages: ReadonlyArray<AdminUsage>): AdminUsage {
  return usages.reduce<AdminUsage>(
    (total, current) => ({
      cachedInputTokens: total.cachedInputTokens + current.cachedInputTokens,
      costUsd:
        Math.round((total.costUsd + current.costUsd) * 1_000_000) / 1_000_000,
      inputTokens: total.inputTokens + current.inputTokens,
      outputTokens: total.outputTokens + current.outputTokens,
      reasoningTokens: total.reasoningTokens + current.reasoningTokens,
      totalTokens: total.totalTokens + current.totalTokens,
      unknownCostRuns: total.unknownCostRuns + current.unknownCostRuns,
    }),
    emptyAdminUsage,
  );
}

function usageText(usage: Usage): string {
  const tokens = usage.totalTokens.toLocaleString("ru-RU");
  return ` · ${tokens} токенов · ${formatCostUsd(usage.costUsd)}`;
}

const root = document.querySelector("#root");
if (root === null) {
  throw new Error("root element not found");
}
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

function introHint(inGuide: boolean, typedByHand: boolean): string {
  if (typedByHand) {
    return inGuide
      ? "В методичке есть раздел «Вступление…», но поле заполнено вручную — оставлено как есть."
      : "В методичке нет раздела «Вступление…» — остаётся вписанное вручную.";
  }
  return inGuide
    ? "Взято из раздела методички «Вступление…» — можно поправить."
    : "В методичке нет раздела «Вступление…» — человек увидит стандартный текст.";
}
